// src/Checkout.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./styles/Checkout.css"; // ✅ IMPORT CSS

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8080";

const REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "email",
  "address1",
  "city",
  "state",
  "postcode",
  "country",
];

export default function Checkout() {
  const plan = "yearly";
  const priceLabel = "Yearly — $36 / year ($3 / month)";
  const cartTitle = "Yearly Subscription";
  const total = 36.0;

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postcode: "",
    country: "AU",
  });

  const [attempted, setAttempted] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const missing = useMemo(() => {
    const m = {};
    for (const k of REQUIRED_FIELDS) {
      if (!String(form[k] || "").trim()) m[k] = true;
    }
    return m;
  }, [form]);

  const hasMissing = Object.keys(missing).length > 0;

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function onCheckout() {
    setAttempted(true);
    setErrMsg("");

    if (hasMissing) {
      setErrMsg("Please fill out all required fields to continue.");
      return;
    }

    setLoading(true);

    try {
      sessionStorage.setItem(
        "cz_checkout_customer",
        JSON.stringify({ plan, customer: form })
      );

      const res = await fetch(`${API_BASE}/api/payments/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, email: form.email, customer: form }),
      });

      const data = await res.json();

      if (!res.ok || !data?.ok || !data?.url) {
        setErrMsg("Checkout could not start. Please try again.");
        setLoading(false);
        return;
      }

      window.location.href = data.url;
    } catch {
      setErrMsg("Checkout could not start. Please try again.");
      setLoading(false);
    }
  }

  const fieldClass = (name) =>
    `czInput ${attempted && missing[name] ? "czInput--err" : ""}`;

  return (
    <div className="czWrap">
      <div className="czCard">
        <div className="czHeader">
          <div>
            <div className="czBrand">My Chinese Horoscope</div>
            <div className="czSub">Secure checkout</div>
          </div>
          <div className="czGuest">
            <span>Guest checkout</span>
            <input className="czGuestInput" />
          </div>
        </div>

        <div className="czGrid">
          {/* LEFT */}
          <div className="czPanel">
            <h1 className="czTitle">Checkout details</h1>
            <div className="czPlan">Plan: <b>{priceLabel}</b></div>

            {errMsg && <div className="czErrorBox">{errMsg}</div>}

            <div className="czRow2">
              <input className={fieldClass("firstName")} placeholder="First name *"
                value={form.firstName} onChange={(e) => setField("firstName", e.target.value)} />
              <input className={fieldClass("lastName")} placeholder="Last name *"
                value={form.lastName} onChange={(e) => setField("lastName", e.target.value)} />
            </div>

            <div className="czRow2">
              <input className={fieldClass("email")} placeholder="Email *"
                value={form.email} onChange={(e) => setField("email", e.target.value)} />
              <input className="czInput" placeholder="Phone"
                value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
            </div>

            <input className={fieldClass("address1")} placeholder="Address line 1 *"
              value={form.address1} onChange={(e) => setField("address1", e.target.value)} />

            <input className="czInput" placeholder="Address line 2"
              value={form.address2} onChange={(e) => setField("address2", e.target.value)} />

            <div className="czRow3">
              <input className={fieldClass("city")} placeholder="City *"
                value={form.city} onChange={(e) => setField("city", e.target.value)} />
              <input className={fieldClass("state")} placeholder="State *"
                value={form.state} onChange={(e) => setField("state", e.target.value)} />
              <input className={fieldClass("postcode")} placeholder="Postcode *"
                value={form.postcode} onChange={(e) => setField("postcode", e.target.value)} />
            </div>

            <input className={fieldClass("country")} placeholder="Country *"
              value={form.country} onChange={(e) => setField("country", e.target.value)} />

            <div className="czLinks">
              <Link to="/subscribe" className="czLink">← Change plan</Link>
              <Link to="/" className="czLink">Back to home</Link>
            </div>
          </div>

          {/* RIGHT */}
          <div className="czPanel czPanel--cart">
            <div className="czCartHeader">
              <h2 className="czCartTitle">Cart</h2>
              <div className="czCartCount">1 item</div>
            </div>

            <div className="czCartLine">
              <div>
                <div className="czCartItem">{cartTitle}</div>
                <div className="czCartSub">$36 / year</div>
                <div className="czCartSub">$3 / month</div>
              </div>
              <div className="czCartPrice">${total.toFixed(2)}</div>
            </div>

            <div className="czDivider" />

            <div className="czCartTotal">
              <div>Total</div>
              <div className="czCartPrice">${total.toFixed(2)}</div>
            </div>

            <button
              className="czCheckoutBtn"
              onClick={onCheckout}
              disabled={loading}
            >
              {loading ? "Redirecting…" : "Checkout"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
